/**
 * Task Management - Core Task Models
 * Matches backend API structure for polymorphic task system
 */

// ==================== Enums ====================

export enum TaskPriority {
  CRITICAL = 1,
  HIGH = 2,
  NORMAL = 3,
  LOW = 4,
}

export const PRIORITY_LABELS: { [key: number]: string } = {
  [TaskPriority.CRITICAL]: 'Critical',
  [TaskPriority.HIGH]: 'High',
  [TaskPriority.NORMAL]: 'Medium',
  [TaskPriority.LOW]: 'Low',
};

export const PRIORITY_COLORS: { [key: number]: string } = {
  [TaskPriority.CRITICAL]: '#DC2626', // red-600
  [TaskPriority.HIGH]: '#F59E0B', // amber-500
  [TaskPriority.NORMAL]: '#3B82F6', // blue-500
  [TaskPriority.LOW]: '#6B7280', // gray-500
};

export enum LinkType {
  PRIMARY = 'primary',
  RELATED = 'related',
  REFERENCE = 'reference',
}

export enum ChangeType {
  STATUS_CHANGE = 'status_change',
  ASSIGNMENT = 'assignment',
  PRIORITY = 'priority',
  DUE_DATE = 'due_date',
}

// Supported entity types that tasks can link to
export type TaskEntityType =
  | 'patient'
  | 'provider'
  | 'session'
  | 'encounter'
  | 'billing_encounter'
  | 'claim'
  | 'eob'
  | 'payor'
  | 'authorization'
  | 'chat_thread'
  | 'user'
  | 'transaction'
  | 'invoice'
  | 'document'
  | 'incident_report';

// ==================== Task Entity Link Models ====================

export interface TaskEntityLink {
  entity_type: TaskEntityType;
  entity_id: string;
  link_type: LinkType;
  metadata?: { [key: string]: any };
}

export interface TaskEntityLinkResponse extends TaskEntityLink {
  id: number;
  task_id: number;
  created_at: string;
  created_by?: number;
}

export interface EntityResolveResponse {
  entity_type: string;
  entity_id: string;
  resolved_name: string | null; // Human name of the specific record (e.g., "John Smith")
  display_name: string | null; // Registry label for the type (e.g., "Patient")
  icon: string | null; // PrimeNG icon class (e.g., "pi-user")
  color: string | null; // Hex color for badges
  web_route: string | null; // Angular route with id already substituted
  mobile_route_name: string | null; // Flutter GoRouter named route
  mobile_route_params_resolved: string | null; // Flutter params JSON with id substituted
}

// ==================== Task Models ====================

export interface CreateTaskRequest {
  task_type_id: number;
  status_id: number;
  priority: TaskPriority;
  subject: string;
  description?: string;
  assigned_to?: number;
  due_at?: string;
  entities?: TaskEntityLink[];
  project_ids?: number[];
}

export interface UpdateTaskRequest {
  subject?: string;
  description?: string;
  priority?: TaskPriority;
  assigned_to?: number;
  due_at?: string;
  entities?: TaskEntityLink[];
  project_ids?: number[];
}

export interface TaskResponse {
  id: number;
  task_type_id: number;
  status_id: number;
  priority: TaskPriority;
  subject: string;
  description?: string;
  assigned_to?: number;
  assigned_at?: string;
  created_at: string;
  created_by?: number;
  updated_at: string;
  updated_by?: number;
  due_at?: string;
  completed_at?: string;
  completed_by?: number;
  last_status_change_at?: string;
  is_deleted: boolean;
}

export interface TaskWithDetailsResponse extends TaskResponse {
  task_type_code?: string;
  task_type_name?: string;
  task_group_id?: number;
  task_group_code?: string;
  task_group_name?: string;
  task_group_color?: string;
  status_code?: string;
  status_name?: string;
  status_is_closed?: boolean;
  status_color?: string;
  assigned_to_name?: string;
  is_overdue: boolean;
  entities?: TaskEntityLinkResponse[];
  projects?: TaskProjectInfo[];
  // These are populated when fetching with full details
  comments?: any[];
  history?: TaskHistoryResponse[];
}

export interface TaskProjectInfo {
  id: number;
  name: string;
  color?: string;
}

// ==================== Task History Models ====================

export interface ChangeTaskStatusRequest {
  to_status_id: number;
  assigned_to?: number;
  note?: string;
}

export interface CompleteTaskRequest {
  note?: string;
}

export interface TaskHistoryResponse {
  id: number;
  task_id: number;
  from_status_id?: number;
  from_status_code?: string;
  to_status_id?: number;
  to_status_code?: string;
  from_user_id?: number;
  to_user_id?: number;
  change_type: ChangeType;
  note?: string;
  changed_at: string;
  changed_by: number;
  // Optional user display info
  changed_by_name?: string;
}

// ==================== Filter/Search Models ====================

export interface TaskFilters {
  status_id?: number | number[];
  task_type_id?: number | number[];
  group_id?: number | number[];
  assigned_to?: number;
  created_by?: number;
  priority?: TaskPriority | TaskPriority[];
  entity_type?: TaskEntityType;
  entity_id?: string;
  project_id?: number | number[];
  is_overdue?: boolean;
  is_closed?: boolean;
  due_after?: string;
  due_before?: string;
  created_after?: string;
  created_before?: string;
  include_deleted?: boolean;
  search?: string;
}

export interface PaginationParams {
  page: number;
  page_size: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
}

export interface PaginatedTasksResponse {
  items: TaskWithDetailsResponse[];
  total: number;
  page: number;
  page_size: number;
  group_counts?: GroupCount[];
  total_pages: number;
}

export interface GroupCount {
  task_group_id: number;
  task_group_name: string;
  count: number;
}

// ==================== Helper Types ====================

export interface TaskCreateContext {
  entityType?: TaskEntityType;
  entityId?: string;
  entityDisplayName?: string;
  linkType?: LinkType;
  metadata?: { [key: string]: any };
  /** Additional entity links (e.g. patient, payor, auth as related). */
  relatedEntities?: TaskEntityLink[];
  suggestedTaskTypeId?: number;
  suggestedPriority?: TaskPriority;
}

export interface TaskSummaryStats {
  total: number;
  open: number;
  in_progress: number;
  completed: number;
  overdue: number;
  critical: number;
  high: number;
}

// ==================== Settings Models ====================

export interface TaskTypeGroup {
  id: number;
  code: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  is_active: boolean;
}

export interface CreateTaskTypeGroupRequest {
  code: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  is_active?: boolean;
}

export interface UpdateTaskTypeGroupRequest {
  code?: string;
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  is_active?: boolean;
}

export interface TaskType {
  id: number;
  group_id: number;
  code: string;
  name: string;
  description?: string;
  default_due_days?: number;
  default_priority: number;
  requires_linked_entity: boolean;
  allowed_entity_types?: string[];
  is_active: boolean;
}

export interface CreateTaskTypeRequest {
  group_id: number;
  code: string;
  name: string;
  description?: string;
  default_due_days?: number;
  default_priority?: number;
  requires_linked_entity?: boolean;
  allowed_entity_types?: string[];
}

export interface UpdateTaskTypeRequest {
  group_id?: number;
  code?: string;
  name?: string;
  description?: string;
  default_due_days?: number;
  default_priority?: number;
  requires_linked_entity?: boolean;
  allowed_entity_types?: string[];
  is_active?: boolean;
}

export interface TaskStatus {
  id: number;
  group_id?: number;
  code: string;
  name: string;
  description?: string;
  is_closed: boolean;
  color?: string;
  sort_order: number;
  is_active: boolean;
}

export interface CreateTaskStatusRequest {
  group_id?: number;
  code: string;
  name: string;
  description?: string;
  is_closed?: boolean;
  color?: string;
  sort_order?: number;
}

export interface UpdateTaskStatusRequest {
  group_id?: number;
  code?: string;
  name?: string;
  description?: string;
  is_closed?: boolean;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

export const TaskColumns = [
  { label: 'ID', value: 'id', disabled: true },
  { label: 'Subject', value: 'subject', disabled: true },
  { label: 'Task Type', value: 'task_type_name' },
  { label: 'Task Group', value: 'group_name' },
  { label: 'Status', value: 'status_name' },
  { label: 'Priority', value: 'priority' },
  { label: 'Due Date', value: 'due_at' },
  { label: 'Assigned To', value: 'assigned_to_name' },
  { label: 'Projects', value: 'projects' },
  { label: 'Entity Links', value: 'entities' },
  { label: 'Created', value: 'created_at' }
];